import * as THREE from 'three';

/**
 * BossZombieModel - A more detailed but still low-poly boss zombie
 * with glowing blue crystals, blue spikes, and advanced animations.
 * Based on the standard ZombieModel but more intimidating.
 */
export class BossZombieModel extends THREE.Group {
    constructor(options = {}) {
        super();
        
        // Configuration options with defaults
        this.config = {
            bodyColor: options.bodyColor || 0x3d3d3d, // Darker gray body
            bodyEmissive: options.bodyEmissive || 0x001000, // Slight green glow
            eyeColor: options.eyeColor || 0x00ccff, // Changed to blue eyes
            eyeEmissive: options.eyeEmissive || 0x00ccff, // Blue glow
            eyeIntensity: options.eyeIntensity || 1.0, // Brighter eyes than regular zombies
            clothesColor: options.clothesColor || 0x1a1a1a, // Darker clothes
            glowColor: options.glowColor || 0x00aaff, // Changed to blue glow for crystals
            glowEmissive: options.glowEmissive || 0x00aaff, // Blue emissive
            glowIntensity: options.glowIntensity || 0.9,
            scale: options.scale || 0.8, // Larger than regular zombies
            animationSpeed: options.animationSpeed || 0.6 // Slightly slower for imposing effect
        };
        
        // Animation state
        this.animationState = {
            walking: true,
            attacking: false,
            attackCooldown: 0,
            specialAttack: false,
            specialAttackCooldown: 0,
            time: 0
        };
        
        // Create body parts
        this.createBodyParts();
        
        // Set up structure
        this.assembleModel();
        
        // Apply initial scale
        this.scale.set(this.config.scale, this.config.scale, this.config.scale);
    }
    
    /**
     * Create all individual body parts meshes
     */
    createBodyParts() {
        // Create materials
        this.bodyMaterial = new THREE.MeshStandardMaterial({ 
            color: this.config.bodyColor,
            roughness: 0.9,
            metalness: 0.2,
            emissive: this.config.bodyEmissive,
            emissiveIntensity: 0.1
        });
        
        this.clothesMaterial = new THREE.MeshStandardMaterial({ 
            color: this.config.clothesColor,
            roughness: 0.8,
            metalness: 0.1
        });
        
        // Create glowing eye material
        this.eyeMaterial = new THREE.MeshStandardMaterial({ 
            color: this.config.eyeColor,
            emissive: this.config.eyeEmissive,
            emissiveIntensity: this.config.eyeIntensity,
            roughness: 0.2,
            metalness: 0.8
        });
        
        // Create glowing crystal/spike material
        this.glowMaterial = new THREE.MeshStandardMaterial({
            color: this.config.glowColor,
            emissive: this.config.glowEmissive,
            emissiveIntensity: this.config.glowIntensity,
            roughness: 0.2,
            metalness: 0.7,
            transparent: true,
            opacity: 0.9
        });
        
        // Create spike material - changed to blue
        this.spikeMaterial = new THREE.MeshStandardMaterial({
            color: 0x0066aa, // Changed to blue spikes
            emissive: 0x003366, // Slight blue glow
            emissiveIntensity: 0.3,
            roughness: 0.7,
            metalness: 0.5 // Increased metalness for shinier appearance
        });
        
        // Head with separate eyes - larger than regular zombies
        this.head = new THREE.Mesh(
            new THREE.BoxGeometry(0.7, 0.7, 0.7),
            this.bodyMaterial
        );
        
        // Eyes (as separate meshes for glow effect)
        this.leftEye = new THREE.Mesh(
            new THREE.SphereGeometry(0.1, 8, 8), // Larger eyes
            this.eyeMaterial
        );
        
        this.rightEye = new THREE.Mesh(
            new THREE.SphereGeometry(0.1, 8, 8),
            this.eyeMaterial
        );
        
        // Torso - bulkier than regular zombies
        this.torso = new THREE.Mesh(
            new THREE.BoxGeometry(1.0, 1.4, 0.6),
            this.bodyMaterial
        );
        
        // Arms - right arm is bigger (asymmetric design)
        this.upperRightArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.5, 0.9, 0.5), // Bigger right arm
            this.bodyMaterial
        );
        
        this.lowerRightArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.45, 0.8, 0.45), // Bigger right forearm
            this.bodyMaterial
        );
        
        this.upperLeftArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.35, 0.8, 0.35), // Normal sized left arm
            this.bodyMaterial
        );
        
        this.lowerLeftArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.3, 0.7, 0.3),
            this.bodyMaterial
        );
        
        // Legs - thicker
        this.upperRightLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.35, 0.8, 0.35),
            this.clothesMaterial
        );
        
        this.lowerRightLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.3, 0.7, 0.3),
            this.bodyMaterial
        );
        
        this.upperLeftLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.35, 0.8, 0.35),
            this.clothesMaterial
        );
        
        this.lowerLeftLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.3, 0.7, 0.3),
            this.bodyMaterial
        );
        
        // Create spikes and glowing crystalline growths
        this.spikes = [];
        this.glowingOrbs = [];
        
        // Create spikes for right shoulder/side/arm (asymmetric design)
        for (let i = 0; i < 20; i++) { // Increased from 12 to 20 spikes
            const height = 0.3 + Math.random() * 0.2; // Varied heights
            const radius = 0.05 + Math.random() * 0.05; // Varied radius
            const spike = new THREE.Mesh(
                new THREE.ConeGeometry(radius, height, 4),
                this.spikeMaterial
            );
            this.spikes.push(spike);
        }
        
        // Create glowing orbs/crystals
        for (let i = 0; i < 18; i++) { // Increased from 15 to 18 orbs
            const size = 0.08 + Math.random() * 0.08; // Varied sizes
            const detail = Math.random() > 0.5 ? 1 : 0; // Varied detail level
            const geometry = Math.random() > 0.3 ? 
                new THREE.OctahedronGeometry(size, detail) : // Crystal shapes
                new THREE.SphereGeometry(size, 6, 6); // Some spherical orbs
            
            const orb = new THREE.Mesh(
                geometry,
                this.glowMaterial.clone() // Clone material to allow individual color variations
            );
            
            // Slight random color variation for each orb (now in blue range)
            if (Math.random() > 0.5) {
                orb.material.color.setHSL(
                    0.6 + Math.random() * 0.1, // Hue in blue range (0.6-0.7)
                    0.8, // Saturation
                    0.5 + Math.random() * 0.2 // Lightness
                );
                orb.material.emissive.copy(orb.material.color);
            }
            
            this.glowingOrbs.push(orb);
        }
    }
    
    /**
     * Assemble the model with proper hierarchy
     */
    assembleModel() {
        // Create joints (empty groups for rotation)
        this.neck = new THREE.Group();
        this.rightShoulder = new THREE.Group();
        this.leftShoulder = new THREE.Group();
        this.rightElbow = new THREE.Group();
        this.leftElbow = new THREE.Group();
        this.rightHip = new THREE.Group();
        this.leftHip = new THREE.Group();
        this.rightKnee = new THREE.Group();
        this.leftKnee = new THREE.Group();
        
        // Add parts to the model with proper hierarchy
        
        // Head and neck
        this.neck.position.set(0, 0.9, 0);
        this.neck.add(this.head);
        this.head.position.set(0, 0.4, 0);
        
        // Add eyes to head
        this.leftEye.position.set(0.18, 0.1, 0.36);
        this.rightEye.position.set(-0.18, 0.1, 0.36);
        this.head.add(this.leftEye);
        this.head.add(this.rightEye);
        
        // Add neck to torso
        this.torso.add(this.neck);
        
        // Right arm (from zombie's perspective) - the bigger arm
        this.rightShoulder.position.set(-0.6, 0.7, 0);
        this.rightShoulder.add(this.upperRightArm);
        this.upperRightArm.position.set(0, -0.45, 0); // Adjusted for bigger size
        this.torso.add(this.rightShoulder);
        
        this.rightElbow.position.set(0, -0.9, 0);
        this.rightElbow.add(this.lowerRightArm);
        this.lowerRightArm.position.set(0, -0.4, 0);
        this.upperRightArm.add(this.rightElbow);
        
        // Left arm
        this.leftShoulder.position.set(0.6, 0.7, 0);
        this.leftShoulder.add(this.upperLeftArm);
        this.upperLeftArm.position.set(0, -0.4, 0);
        this.torso.add(this.leftShoulder);
        
        this.leftElbow.position.set(0, -0.8, 0);
        this.leftElbow.add(this.lowerLeftArm);
        this.lowerLeftArm.position.set(0, -0.4, 0);
        this.upperLeftArm.add(this.leftElbow);
        
        // Right leg
        this.rightHip.position.set(-0.3, -0.7, 0);
        this.rightHip.add(this.upperRightLeg);
        this.upperRightLeg.position.set(0, -0.4, 0);
        this.torso.add(this.rightHip);
        
        this.rightKnee.position.set(0, -0.8, 0);
        this.rightKnee.add(this.lowerRightLeg);
        this.lowerRightLeg.position.set(0, -0.4, 0);
        this.upperRightLeg.add(this.rightKnee);
        
        // Left leg
        this.leftHip.position.set(0.3, -0.7, 0);
        this.leftHip.add(this.upperLeftLeg);
        this.upperLeftLeg.position.set(0, -0.4, 0);
        this.torso.add(this.leftHip);
        
        this.leftKnee.position.set(0, -0.8, 0);
        this.leftKnee.add(this.lowerLeftLeg);
        this.lowerLeftLeg.position.set(0, -0.4, 0);
        this.upperLeftLeg.add(this.leftKnee);
        
        // Add torso to the root of the model
        this.add(this.torso);
        
        // Center the model so the feet are at y=0
        this.torso.position.set(0, 2.1, 0); // Higher due to larger body
        
        // Add spikes to right shoulder/arm/side (as in concept art)
        // Main shoulder spike
        this.spikes[0].position.set(-0.3, 0.8, 0);
        this.spikes[0].rotation.z = -Math.PI / 6;
        this.torso.add(this.spikes[0]);
        
        // Shoulder spike pointing outward
        this.spikes[1].position.set(-0.6, 0.6, 0);
        this.spikes[1].rotation.z = -Math.PI / 2;
        this.torso.add(this.spikes[1]);
        
        // Upper back spike
        this.spikes[2].position.set(-0.3, 0.6, -0.3);
        this.spikes[2].rotation.x = -Math.PI / 3;
        this.spikes[2].rotation.z = -Math.PI / 6;
        this.torso.add(this.spikes[2]);
        
        // Lower back spike
        this.spikes[3].position.set(-0.3, 0, -0.3);
        this.spikes[3].rotation.x = -Math.PI / 4;
        this.spikes[3].rotation.z = -Math.PI / 6;
        this.torso.add(this.spikes[3]);
        
        // Head spike
        this.spikes[4].position.set(0, 0.35, 0);
        this.spikes[4].rotation.x = 0;
        this.head.add(this.spikes[4]);
        
        // Right upper arm spikes (multiple) - on the bigger arm
        this.spikes[5].position.set(0, -0.2, 0.25);
        this.spikes[5].rotation.x = Math.PI / 2;
        this.upperRightArm.add(this.spikes[5]);
        
        this.spikes[6].position.set(0.2, -0.3, 0.15);
        this.spikes[6].rotation.x = Math.PI / 3;
        this.spikes[6].rotation.z = Math.PI / 3;
        this.upperRightArm.add(this.spikes[6]);
        
        this.spikes[7].position.set(-0.2, -0.1, 0.2);
        this.spikes[7].rotation.x = Math.PI / 2.5;
        this.spikes[7].rotation.z = -Math.PI / 4;
        this.upperRightArm.add(this.spikes[7]);
        
        // Right forearm spikes
        this.spikes[8].position.set(0, -0.1, 0.23);
        this.spikes[8].rotation.x = Math.PI / 2;
        this.lowerRightArm.add(this.spikes[8]);
        
        this.spikes[9].position.set(0.2, -0.3, 0);
        this.spikes[9].rotation.z = Math.PI / 2;
        this.lowerRightArm.add(this.spikes[9]);
        
        // Torso additional spikes
        this.spikes[10].position.set(0.3, 0.4, -0.3);
        this.spikes[10].rotation.x = -Math.PI / 3;
        this.torso.add(this.spikes[10]);
        
        this.spikes[11].position.set(0, -0.5, -0.3);
        this.spikes[11].rotation.x = -Math.PI / 2;
        this.torso.add(this.spikes[11]);
        
        // Added additional spikes (12-19)
        // More spikes on the oversized right arm
        this.spikes[12].position.set(0.1, -0.5, 0.2);
        this.spikes[12].rotation.x = Math.PI / 2;
        this.spikes[12].rotation.z = Math.PI / 6;
        this.upperRightArm.add(this.spikes[12]);
        
        this.spikes[13].position.set(-0.15, -0.45, 0.2);
        this.spikes[13].rotation.x = Math.PI / 2;
        this.spikes[13].rotation.z = -Math.PI / 6;
        this.upperRightArm.add(this.spikes[13]);
        
        // More spikes on the right forearm
        this.spikes[14].position.set(-0.15, -0.3, 0.2);
        this.spikes[14].rotation.x = Math.PI / 2;
        this.spikes[14].rotation.z = -Math.PI / 8;
        this.lowerRightArm.add(this.spikes[14]);
        
        this.spikes[15].position.set(0.15, 0, 0.2);
        this.spikes[15].rotation.x = Math.PI / 2;
        this.spikes[15].rotation.z = Math.PI / 8;
        this.lowerRightArm.add(this.spikes[15]);
        
        // Head spikes for more menacing look
        this.spikes[16].position.set(-0.2, 0.3, 0.15);
        this.spikes[16].rotation.x = Math.PI / 4;
        this.spikes[16].rotation.z = -Math.PI / 8;
        this.head.add(this.spikes[16]);
        
        this.spikes[17].position.set(0.2, 0.3, 0.15);
        this.spikes[17].rotation.x = Math.PI / 4;
        this.spikes[17].rotation.z = Math.PI / 8;
        this.head.add(this.spikes[17]);
        
        // Shoulder spikes
        this.spikes[18].position.set(-0.45, 0.75, -0.15);
        this.spikes[18].rotation.x = -Math.PI / 6;
        this.spikes[18].rotation.z = -Math.PI / 4;
        this.torso.add(this.spikes[18]);
        
        this.spikes[19].position.set(-0.5, 0.4, 0.1);
        this.spikes[19].rotation.z = -Math.PI / 3;
        this.torso.add(this.spikes[19]);
        
        // Add glowing orbs/crystals (matching concept art)
        // Shoulder cluster
        this.glowingOrbs[0].position.set(-0.5, 0.7, 0.1);
        this.glowingOrbs[0].rotation.set(Math.random(), Math.random(), Math.random());
        this.torso.add(this.glowingOrbs[0]);
        
        this.glowingOrbs[1].position.set(-0.45, 0.6, 0.15);
        this.glowingOrbs[1].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[1].scale.set(0.7, 0.7, 0.7);
        this.torso.add(this.glowingOrbs[1]);
        
        // Back clusters
        this.glowingOrbs[2].position.set(-0.2, 0.5, -0.3);
        this.glowingOrbs[2].rotation.set(Math.random(), Math.random(), Math.random());
        this.torso.add(this.glowingOrbs[2]);
        
        this.glowingOrbs[3].position.set(-0.3, 0.3, -0.3);
        this.glowingOrbs[3].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[3].scale.set(0.8, 0.8, 0.8);
        this.torso.add(this.glowingOrbs[3]);
        
        // Upper arm crystals - big arm has more growth
        this.glowingOrbs[4].position.set(0.05, -0.3, 0.25);
        this.glowingOrbs[4].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[4].scale.set(0.7, 0.7, 0.7);
        this.upperRightArm.add(this.glowingOrbs[4]);
        
        this.glowingOrbs[5].position.set(-0.18, -0.15, 0.25);
        this.glowingOrbs[5].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[5].scale.set(0.65, 0.65, 0.65);
        this.upperRightArm.add(this.glowingOrbs[5]);
        
        this.glowingOrbs[6].position.set(0.18, -0.4, 0.22);
        this.glowingOrbs[6].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[6].scale.set(0.6, 0.6, 0.6);
        this.upperRightArm.add(this.glowingOrbs[6]);
        
        // Lower right arm (forearm) orbs
        this.glowingOrbs[7].position.set(0.15, -0.25, 0.22);
        this.glowingOrbs[7].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[7].scale.set(0.5, 0.5, 0.5);
        this.lowerRightArm.add(this.glowingOrbs[7]);
        
        // Side torso
        this.glowingOrbs[8].position.set(-0.5, 0.2, 0);
        this.glowingOrbs[8].rotation.set(Math.random(), Math.random(), Math.random());
        this.torso.add(this.glowingOrbs[8]);
        
        // Lower back
        this.glowingOrbs[9].position.set(-0.2, -0.2, -0.3);
        this.glowingOrbs[9].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[9].scale.set(0.7, 0.7, 0.7);
        this.torso.add(this.glowingOrbs[9]);
        
        // Right leg
        this.glowingOrbs[10].position.set(0.1, -0.3, 0.18);
        this.glowingOrbs[10].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[10].scale.set(0.6, 0.6, 0.6);
        this.upperRightLeg.add(this.glowingOrbs[10]);
        
        // Head orbs
        this.glowingOrbs[11].position.set(0.2, 0.3, 0.2);
        this.glowingOrbs[11].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[11].scale.set(0.4, 0.4, 0.4);
        this.head.add(this.glowingOrbs[11]);
        
        // Additional back orbs
        this.glowingOrbs[12].position.set(0.2, 0.4, -0.31);
        this.glowingOrbs[12].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[12].scale.set(0.75, 0.75, 0.75);
        this.torso.add(this.glowingOrbs[12]);
        
        this.glowingOrbs[13].position.set(-0.1, -0.4, -0.31);
        this.glowingOrbs[13].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[13].scale.set(0.65, 0.65, 0.65);
        this.torso.add(this.glowingOrbs[13]);
        
        // Left shoulder orb
        this.glowingOrbs[14].position.set(0.4, 0.5, 0.1);
        this.glowingOrbs[14].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[14].scale.set(0.55, 0.55, 0.55);
        this.torso.add(this.glowingOrbs[14]);
        
        // Additional orbs (15-17) for more blue crystal coverage
        // Extra orb in the big right arm
        this.glowingOrbs[15].position.set(-0.1, -0.6, 0.2);
        this.glowingOrbs[15].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[15].scale.set(0.6, 0.6, 0.6);
        this.upperRightArm.add(this.glowingOrbs[15]);
        
        // Extra orb on the chest
        this.glowingOrbs[16].position.set(0, 0.4, 0.31);
        this.glowingOrbs[16].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[16].scale.set(0.5, 0.5, 0.5);
        this.torso.add(this.glowingOrbs[16]);
        
        // Extra orb on the head
        this.glowingOrbs[17].position.set(-0.2, 0.3, 0.25);
        this.glowingOrbs[17].rotation.set(Math.random(), Math.random(), Math.random());
        this.glowingOrbs[17].scale.set(0.4, 0.4, 0.4);
        this.head.add(this.glowingOrbs[17]);
        
        // Set initial zombie pose (slightly hunched, arms forward)
        this.setBossZombiePose();
        
        // Add subtle pulsating animation to the glowing orbs
        this.animateGlowingOrbs();
    }
    
    /**
     * Set the default boss zombie pose - more imposing than regular zombies
     */
    setBossZombiePose() {
        // More upright but still hunched
        this.torso.rotation.x = 0.15;
        this.neck.rotation.x = -0.15;
        
        // Arms more bent ready to attack, right arm slightly raised
        this.rightShoulder.rotation.z = -0.4;
        this.leftShoulder.rotation.z = 0.4;
        this.rightShoulder.rotation.x = -0.4; // Raise right arm more
        this.leftShoulder.rotation.x = -0.3;
        
        // Bend elbows
        this.rightElbow.rotation.x = -0.7; // Bend right elbow more
        this.leftElbow.rotation.x = -0.5;
        
        // Angle legs for a wider stance
        this.rightHip.rotation.z = 0.1;
        this.leftHip.rotation.z = -0.1;
        this.rightHip.rotation.x = -0.1;
        this.leftHip.rotation.x = -0.1;
    }
    
    /**
     * Create a subtle pulsating animation for the glowing orbs
     */
    animateGlowingOrbs() {
        // Each orb will pulse slightly out of sync for an organic feel
        this.glowingOrbs.forEach((orb, i) => {
            const basePulse = 0.15; // Base pulse amount
            const phaseOffset = i * 0.3; // Offset each orb's pulse
            
            // Store original scale for reference
            orb.userData.baseScale = orb.scale.x;
        });
    }
    
    /**
     * Update animations based on elapsed time
     * @param {number} deltaTime - Time since last frame in seconds
     */
    update(deltaTime) {
        // Accumulate animation time
        this.animationState.time += deltaTime * this.config.animationSpeed;
        
        // Handle attack cooldowns
        if (this.animationState.attackCooldown > 0) {
            this.animationState.attackCooldown -= deltaTime;
            if (this.animationState.attackCooldown <= 0) {
                this.animationState.attacking = false;
            }
        }
        
        if (this.animationState.specialAttackCooldown > 0) {
            this.animationState.specialAttackCooldown -= deltaTime;
            if (this.animationState.specialAttackCooldown <= 0) {
                this.animationState.specialAttack = false;
            }
        }
        
        // Animate based on current state
        if (this.animationState.specialAttack) {
            this.animateSpecialAttack(this.animationState.time);
        } else if (this.animationState.attacking) {
            this.animateBossAttack(this.animationState.time);
        } else {
            this.animateBossWalk(this.animationState.time);
        }
        
        // Always animate the glowing orbs pulsating
        this.updateGlowingOrbs(this.animationState.time);
    }
    
    /**
     * Update the glowing orbs pulsating and color shifting animations
     */
    updateGlowingOrbs(time) {
        this.glowingOrbs.forEach((orb, i) => {
            // Pulse size based on time
            const baseScale = orb.userData.baseScale || 1;
            const pulseFactor = 0.1 * Math.sin(time * 2 + i * 0.3) + 1;
            orb.scale.set(
                baseScale * pulseFactor,
                baseScale * pulseFactor,
                baseScale * pulseFactor
            );
            
            // Pulse intensity
            if (orb.material) {
                // Slight color shifts for a magical effect
                orb.material.emissiveIntensity = 0.5 + 0.4 * Math.sin(time * 3 + i * 0.4);
                
                // Subtle hue shift in the blue range
                const hue = 0.6 + 0.05 * Math.sin(time + i * 0.2);
                orb.material.emissive.setHSL(hue, 0.8, 0.5);
            }
        });
    }
    
    /**
     * Animate the boss zombie walking - more imposing than regular zombies
     */
    animateBossWalk(time) {
        // Slower, more deliberate shambling walk cycle
        const armSwing = Math.sin(time * 1.7) * 0.12;
        const legSwing = Math.sin(time * 1.7) * 0.15;
        const bodyWobble = Math.sin(time * 0.8) * 0.05;
        
        // Subtle torso wobble
        this.torso.rotation.z = bodyWobble;
        
        // Arm swinging - right arm swings less due to weight
        this.rightShoulder.rotation.x = -0.4 + armSwing * 0.7; // Reduced swing for bigger arm
        this.leftShoulder.rotation.x = -0.3 - armSwing;
        
        // Leg movement
        this.rightHip.rotation.x = -0.1 + legSwing;
        this.leftHip.rotation.x = -0.1 - legSwing;
        
        // Head turning slightly - looking for prey
        this.neck.rotation.y = Math.sin(time * 0.5) * 0.15;
    }
    
    /**
     * Trigger the basic attack animation
     */
    startAttack() {
        // Only start attack if not already attacking
        if (!this.animationState.attacking && !this.animationState.specialAttack) {
            this.animationState.attacking = true;
            this.animationState.attackCooldown = 1.3; // Slower attack animation
            
            // Reset arm positions for attack animation
            this.rightShoulder.rotation.x = -0.7;
            this.leftShoulder.rotation.x = -0.7;
            this.rightElbow.rotation.x = -1.0;
            this.leftElbow.rotation.x = -1.0;
        }
    }
    
    /**
     * Trigger special attack animation
     */
    startSpecialAttack() {
        if (!this.animationState.attacking && !this.animationState.specialAttack) {
            this.animationState.specialAttack = true;
            this.animationState.specialAttackCooldown = 2.0; // Longer special attack
            
            // Reset positions for special attack
            this.torso.rotation.x = 0.3;
            this.rightShoulder.rotation.x = -1.0;
            this.leftShoulder.rotation.x = -1.0;
        }
    }
    
    /**
     * Animate the boss zombie's basic attack - more powerful than regular zombies
     * Uses the bigger right arm as the main weapon
     */
    animateBossAttack(time) {
        // Calculate attack phase (0 to 1)
        const phase = 1 - (this.animationState.attackCooldown / 1.3);
        
        // Attack consists of raising arms and then quickly bringing them down
        if (phase < 0.4) {
            // Wind up phase - raise arms further
            const windUpPhase = phase / 0.4;
            
            // Right arm (bigger) winds up more
            this.rightShoulder.rotation.x = -0.7 - windUpPhase * 1.0;
            this.leftShoulder.rotation.x = -0.7 - windUpPhase * 0.6;
            this.rightElbow.rotation.x = -1.0 - windUpPhase * 0.6;
            this.leftElbow.rotation.x = -1.0 - windUpPhase * 0.4;
            
            // Also lean back slightly
            this.torso.rotation.x = 0.15 - windUpPhase * 0.2;
        } else {
            // Strike phase - bring arms down forcefully
            const strikePhase = (phase - 0.4) / 0.6;
            
            // Right arm swings down harder
            this.rightShoulder.rotation.x = -1.7 + strikePhase * 2.0;
            this.leftShoulder.rotation.x = -1.3 + strikePhase * 1.5;
            this.rightElbow.rotation.x = -1.6 + strikePhase * 0.8;
            this.leftElbow.rotation.x = -1.4 + strikePhase * 0.6;
            
            // Lunge forward powerfully
            this.torso.rotation.x = -0.05 + strikePhase * 0.4;
            this.torso.rotation.z = 0.1 * strikePhase; // Twist slightly for more force
            
            // Also rotate head down with strike for extra menace
            this.neck.rotation.x = -0.15 - strikePhase * 0.4;
        }
    }
    
    /**
     * Animate the boss zombie's special attack - a powerful ground pound
     * Uses the mutated arm for a devastating slam
     */
    animateSpecialAttack(time) {
        // Calculate attack phase (0 to 1)
        const phase = 1 - (this.animationState.specialAttackCooldown / 2.0);
        
        if (phase < 0.3) {
            // Phase 1: Raise both arms high, right arm higher
            const p = phase / 0.3;
            this.rightShoulder.rotation.x = -1.0 - p * 1.2;
            this.leftShoulder.rotation.x = -1.0 - p * 0.8;
            this.rightElbow.rotation.x = -0.5 - p * 1.0;
            this.leftElbow.rotation.x = -0.5 - p * 1.0;
            
            // Lean back 
            this.torso.rotation.x = 0.3 - p * 0.1;
            
            // Prepare to jump
            this.rightHip.rotation.x = -0.2 - p * 0.3;
            this.leftHip.rotation.x = -0.2 - p * 0.3;
        } 
        else if (phase < 0.4) {
            // Phase 2: Jump up slightly
            const p = (phase - 0.3) / 0.1;
            this.position.y = p * 0.5; // Jump height
        }
        else if (phase < 0.6) {
            // Phase 3: In the air, prepare to smash down with right arm
            const p = (phase - 0.4) / 0.2;
            this.position.y = 0.5; // Maintain jump height
            
            // Prepare for impact - bigger right arm raised higher
            this.torso.rotation.x = 0.2 + p * 0.3;
            this.rightShoulder.rotation.x = -2.2 + p * 0.4;
            this.leftShoulder.rotation.x = -1.8 + p * 0.5;
            
            // Elbows bent
            this.rightElbow.rotation.x = -1.5;
            this.leftElbow.rotation.x = -1.5;
        }
        else {
            // Phase 4: Smash down with mutated arm
            const p = (phase - 0.6) / 0.4;
            
            // Come back down
            this.position.y = 0.5 * (1 - p);
            
            // Smash down with arms, right arm slams harder
            this.torso.rotation.x = 0.5 - p * 0.5;
            this.torso.rotation.z = 0.2 * p; // Twist to use right arm more
            
            this.rightShoulder.rotation.x = -1.8 + p * 2.0; // More powerful swing
            this.leftShoulder.rotation.x = -1.3 + p * 1.3;
            this.rightElbow.rotation.x = -1.5 + p * 1.5;
            this.leftElbow.rotation.x = -1.5 + p * 1.5;
            
            // Intensify the glow during impact
            if (p > 0.8) {
                const impactGlow = 3.0 * (1 - (p - 0.8) / 0.2);
                this.glowingOrbs.forEach(orb => {
                    if (orb.material) {
                        orb.material.emissiveIntensity = this.config.glowIntensity * impactGlow;
                    }
                });
            }
        }
    }
    
    /**
     * Helper method to make all meshes cast and receive shadows
     */
    enableShadows() {
        this.traverse(child => {
            if (child instanceof THREE.Mesh) {
                child.castShadow = true;
                child.receiveShadow = true;
            }
        });
    }
} 